/**
 * Tetris-style horizontal compaction for flowchart chains
 * 
 * Pulls chains together horizontally with intelligent packing that considers
 * vertical positions - chains can interlock when they don't overlap vertically.
 */

import { findDisconnectedComponents } from '../../layout';
import { buildDependencyGraph } from '../../layout/graphBuilder';
import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from '../../layout/constants';

/**
 * "Tetris" compaction - pulls chains together horizontally with intelligent packing
 * 
 * Unlike simple bounding-box compaction, this considers vertical positions so that
 * chains can interlock - a narrow top part of one chain can slide next to a wide
 * bottom part of another chain, as long as they don't overlap at the same Y levels.
 * 
 * This preserves relative positions WITHIN each chain - only horizontal position
 * of the entire chain is adjusted.
 * 
 * @param {Object} positions - Map of taskId -> { x, y }
 * @param {Array} tasks - Array of task objects
 * @param {Object} nodeDimensions - Map of taskId -> { width, height }
 * @returns {Object} New positions map with compacted chain positions
 */
export function compactChainsHorizontally(positions, tasks, nodeDimensions) {
  if (!positions || Object.keys(positions).length === 0 || tasks.length === 0) {
    return positions;
  }
  
  // Build dependency graph to find disconnected components (chains)
  const graph = buildDependencyGraph(tasks);
  const components = findDisconnectedComponents(tasks, graph);
  
  if (components.length <= 1) {
    // Only one chain, nothing to compact
    return positions;
  }
  
  // Build detailed node info for each chain (including Y positions)
  const chainData = components.map(component => {
    const nodes = [];
    let minX = Infinity;
    
    component.tasks.forEach(task => {
      const pos = positions[task.id];
      // Skip if no position or if position values are invalid (null, NaN, Infinity)
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
      
      const dims = getNodeDimensions(task.id, nodeDimensions);
      nodes.push({
        id: task.id,
        x: pos.x,
        y: pos.y,
        width: dims.width,
        height: dims.height,
        right: pos.x + dims.width,
        bottom: pos.y + dims.height,
      });
      minX = Math.min(minX, pos.x);
    });
    
    // Skip chains with no positioned tasks or invalid minX
    if (nodes.length === 0 || !Number.isFinite(minX)) {
      return null;
    }
    
    return {
      id: component.id,
      tasks: component.tasks,
      nodes,
      minX,
    };
  }).filter(Boolean);
  
  // If only one or zero chains have positions, nothing to compact
  if (chainData.length <= 1) {
    return positions;
  }
  
  // Sort chains by their leftmost position (minX)
  chainData.sort((a, b) => a.minX - b.minX);
  
  // Build new positions with compacted chains
  const newPositions = { ...positions };
  
  // Track all "placed" nodes (nodes that have been compacted and are now fixed)
  // Start with the first chain's nodes (they don't move)
  const placedNodes = [...chainData[0].nodes];
  
  // Process chains from left to right, starting with the second chain
  for (let i = 1; i < chainData.length; i++) {
    const chain = chainData[i];
    
    // Find the maximum shift left we can do without overlapping any placed nodes
    // We check each node in the chain against all placed nodes at overlapping Y ranges
    const maxShiftLeft = calculateMaxShiftLeft(chain.nodes, placedNodes, HORIZONTAL_GAP);
    
    // Only shift if we can move left
    if (maxShiftLeft > 0) {
      // Shift all nodes in this chain left by maxShiftLeft
      chain.nodes.forEach(node => {
        newPositions[node.id] = {
          x: Math.round(node.x - maxShiftLeft),
          y: node.y,
        };
        // Update the node's position for subsequent calculations
        node.x -= maxShiftLeft;
        node.right -= maxShiftLeft;
      });
      
      chain.minX -= maxShiftLeft;
    }
    
    // Add this chain's nodes to the placed nodes
    placedNodes.push(...chain.nodes);
  }
  
  return newPositions;
}

/**
 * Calculate the maximum amount a chain can shift left without overlapping placed nodes
 * 
 * For each node in the chain, we find all placed nodes that overlap vertically,
 * then calculate the minimum gap. The chain can shift left by the minimum of all these gaps.
 * 
 * @param {Array} chainNodes - Nodes in the chain being shifted
 * @param {Array} placedNodes - Already-placed nodes that can't move
 * @param {number} gap - Minimum horizontal gap to maintain
 * @returns {number} Maximum shift left (0 or positive)
 */
export function calculateMaxShiftLeft(chainNodes, placedNodes, gap) {
  let maxShift = Infinity;
  
  chainNodes.forEach(chainNode => {
    // Find all placed nodes that overlap vertically with this chain node
    const verticallyOverlapping = placedNodes.filter(placed => {
      // Check if Y ranges overlap (with no gap requirement vertically)
      return chainNode.y < placed.bottom && chainNode.bottom > placed.y;
    });
    
    if (verticallyOverlapping.length === 0) {
      // No vertical overlap - this node doesn't constrain horizontal movement
      // But we still need to respect the leftmost boundary (PADDING)
      const shiftToEdge = chainNode.x - PADDING;
      maxShift = Math.min(maxShift, shiftToEdge);
    } else {
      // Find the rightmost edge of overlapping placed nodes
      let rightmostEdge = -Infinity;
      verticallyOverlapping.forEach(placed => {
        rightmostEdge = Math.max(rightmostEdge, placed.right);
      });
      
      // The chain node can shift left until it's `gap` pixels from the rightmost edge
      const targetX = rightmostEdge + gap;
      const possibleShift = chainNode.x - targetX;
      maxShift = Math.min(maxShift, possibleShift);
    }
  });
  
  // Don't shift right (negative shift) or if no valid shift found
  return Math.max(0, maxShift);
}
